import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TrophyIcon } from '@heroicons/react/24/solid';
import { leaderboardAPI, MyLeaderboard } from '../../services/api';

const medal = (rank: number) =>
  rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;

const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<MyLeaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // No month param -> backend uses the current IST month, so the board
    // automatically rolls over when a new month begins.
    leaderboardAPI.getMine()
      .then(setData)
      .catch((e) => setError(
        e?.response?.status === 400
          ? "You're not in a class yet, so there's no leaderboard to show."
          : 'Could not load the leaderboard. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // The board shows the top three only, rather than every student in the class.
  // Ranking is standard competition style (1, 2, 2, 4), so a tie for a medal
  // place legitimately makes this more than three rows.
  const podium = data ? data.entries.filter(e => e.rank <= 3) : [];
  // A student outside the top three still sees their own row, below a divider,
  // so the board answers "where am I?" as well as "who is winning?".
  const you = data ? data.entries.find(e => e.is_you) : undefined;
  const youBelowPodium = you && you.rank > 3 ? you : undefined;

  return (
    <div className="min-h-screen bg-slate-50">
      <header
        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
        // clears the iPhone status bar; resolves to 0 where there is no inset
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg hover:bg-white/15 transition-colors" title="Back">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <TrophyIcon className="h-6 w-6 text-amber-300" />
          <div>
            <h1 className="text-lg font-bold leading-tight">Class Leaderboard</h1>
            <p className="text-xs opacity-90">{data ? `Class ${data.class_label} · ${data.month}` : 'Monthly'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><div className="text-5xl mb-3 animate-bounce">🏆</div>Loading…</div>
        ) : error ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">{error}</div>
        ) : data && (
          <>
            {/* Your rank hero */}
            <div
              className="w-full text-left bg-white rounded-2xl shadow-sm border border-indigo-100 p-5 mb-5 flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex flex-col items-center justify-center flex-shrink-0">
                <span className="text-[10px] leading-none opacity-90">RANK</span>
                <span className="text-xl font-extrabold leading-none">
                  {data.your_rank ? `#${data.your_rank}` : '—'}
                </span>
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-800">Your rank this month</div>
                <div className="text-sm text-slate-500">
                  {data.your_rank
                    ? `You're #${data.your_rank} in Class ${data.class_label}`
                    : 'Read articles and play the Daily Quiz to get on the board!'}
                </div>
                <div className="text-xs text-indigo-500 mt-1">{data.month}</div>
              </div>
            </div>

            {/* Ranked list - rank on the left, name beside it (standard leaderboard) */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-[11px] text-slate-500 uppercase tracking-wider">
                    <th className="text-center font-bold py-3 w-20">Rank</th>
                    <th className="text-left font-bold py-3 pr-4">Student</th>
                  </tr>
                </thead>
                <tbody>
                  {podium.map((e, i) => (
                    <tr key={i}
                      className={`border-t border-slate-100 ${e.is_you ? 'bg-indigo-50' : (i % 2 ? 'bg-slate-50/40' : 'bg-white')}`}>
                      <td className="py-3 w-20 text-center align-middle">
                        {medal(e.rank)
                          ? <span className="text-xl leading-none">{medal(e.rank)}</span>
                          : <span className={`font-extrabold tabular-nums ${e.is_you ? 'text-indigo-600' : 'text-slate-400'}`}>{e.rank}</span>}
                      </td>
                      <td className="py-3 pr-4 text-left align-middle">
                        <span className={`font-semibold ${e.is_you ? 'text-indigo-700' : 'text-slate-700'}`}>
                          {e.name}
                        </span>
                        {e.is_you && (
                          <span className="ml-2 text-[10px] font-bold bg-indigo-600 text-white px-2 py-0.5 rounded-full align-middle">YOU</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {youBelowPodium && (
                    <>
                      <tr className="border-t border-slate-100 bg-white">
                        <td colSpan={2} className="py-1 text-center text-slate-300 text-xs tracking-widest">· · ·</td>
                      </tr>
                      <tr className="border-t border-slate-100 bg-indigo-50">
                        <td className="py-3 w-20 text-center align-middle">
                          <span className="font-extrabold tabular-nums text-indigo-600">{youBelowPodium.rank}</span>
                        </td>
                        <td className="py-3 pr-4 text-left align-middle">
                          <span className="font-semibold text-indigo-700">{youBelowPodium.name}</span>
                          <span className="ml-2 text-[10px] font-bold bg-indigo-600 text-white px-2 py-0.5 rounded-full align-middle">YOU</span>
                        </td>
                      </tr>
                    </>
                  )}
                  {data.entries.length === 0 && (
                    <tr><td colSpan={2} className="text-center py-10 text-slate-400">No activity in your class yet this month.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="text-center text-xs text-slate-400 mt-6 leading-relaxed">
              Your rank is based on the number of articles you read and your Daily Quiz scores this month.
              <br />The leaderboard resets at the start of every month.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default LeaderboardPage;
